import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalized, NavigationGuardNext, RouteLocationGeneric } from 'vue-router'

import PublicJobsLayout from '@/views/public/PublicJobsLayout.vue'
import RegisterView from '@/views/auth/RegisterView.vue'
import LoginView from '@/views/auth/LoginView.vue'
import ForgotPasswordView from '@/views/auth/ForgotPasswordView.vue'
import SettingsView from '@/views/settings/SettingsView.vue'
import DashboardLayout from '@/components/layout/DashboardLayout.vue'
import LandingView from '@/views/LandingView.vue'

import UserApplicationsView from '@/views/dashboard/UserApplicationsView.vue'
import UserApplicationAddContainer from '@/components/commonUserApplications/UserApplicationAddContainer.vue'

import UserApplicationDetails from '@/components/userApplications/UserApplicationDetails.vue'
import UserApplicationEdit from '@/components/userApplications/UserApplicationEdit.vue'
import ReferralEmailPreview from '@/components/referrals/ReferralEmailPreview.vue'

import RolesView from '@/views/dashboard/JobsView.vue'
import JobDetails from '@/components/jobs/JobDetails.vue'
import JobEditView from '@/views/dashboard/JobEditView.vue'
import CompaniesView from '@/views/dashboard/CompaniesView.vue'


import NotFoundView from '@/views/NotFoundView.vue'
import AllApplicationsView from '@/views/manage/AllApplicationsView.vue'
import PermissionDeniedView from '@/views/PermissionDeniedView.vue'
import RecentlyActiveCandidates from '@/views/manage/RecentlyActiveApplicants.vue'

import JobApplicationEdit from '@/components/jobs/JobApplicationEdit.vue'

import ReferrersView from '@/views/manage/ReferrersView.vue'
import ReferrerAddView from '@/views/manage/ReferrerAddView.vue'

import ResetPasswordView from '@/views/auth/ResetPasswordView.vue'
import AuthCallbackView from '@/views/auth/AuthCallbackView.vue'
import CompleteProfileView from '@/views/auth/CompleteProfileView.vue'
import MagicLoginView from '@/views/auth/MagicLoginView.vue'
import PasswordLoginView from '@/views/auth/PasswordLoginView.vue'

import TermsAndConditionsView from '@/views/auth/TermsAndConditionsView.vue'
import PrivacyPolicyView from '@/views/auth/PrivacyPolicyView.vue'

import JobCreateView from '@/views/dashboard/JobCreateView.vue'

import ApplicantView from '@/views/dashboard/ApplicantView.vue'
import ApplicantsView from '@/views/dashboard/ApplicantsView.vue'

import ReferralRequestsView from '@/views/manage/ReferralRequestsView.vue'
import MyReferralsView from '@/views/dashboard/MyReferralsView.vue'
import ReferralActionResultView from '@/views/referralAction/ReferralActionResultView.vue'
import ReferralActionProvideLinkView from '@/views/referralAction/ReferralActionProvideLinkView.vue'

import { decodeJWT } from '@/utils/jwtDecoder'
import { useApplicationsStore } from '@/stores/applications'
import { useAuth } from '@/composables/useAuth'
import { useAuthStore } from '@/stores/auth'
import { PUBLIC_PAGE_NAMES } from '@/const/routes'
import { useSubscriptionStore } from '@/stores/subscriptionAccess'
import { DEFAULT_APP_PLATFORM } from '@/const/platformAccess'
import {
  shouldSkipPlatformAccessCheck
} from '@/router/platformAccessGuard'

declare module 'vue-router' {
  interface RouteMeta {
    /** Subscription product code from access/status `platforms` (default: tracktalent). */
    requiresPlatform?: string
  }
}

const routes = [
  /**
   * Root route - Handles initial authentication and role-based redirects
   * - GUIDE users → /applications/all
   * - FELLOW/MENTOR/COMMON users → /jobs
   *
   * Available roles: COMMON, MENTOR, FELLOW, GUIDE
   */
  {
    path: '/',
    name: 'Landing',
    component: LandingView,
    beforeEnter: async (to: any, from: any, next: any) => {
      const authStore = useAuthStore()

      // Check authentication
      let isAuthenticated = authStore.isAuthenticated
      if (!isAuthenticated) {
        isAuthenticated = await authStore.checkAuth()
      }

      // If authenticated, redirect to appropriate dashboard
      if (isAuthenticated) {
        const userRole = authStore.userRole
        const target = userRole === 'GUIDE' ? '/applications/all' : '/jobs'
        return next({ path: target, query: to.query })
      }

      // Otherwise let them view the landing page
      next()
    }
  },
  /**
   * Main Dashboard Layout - Requires authentication
   * All child routes inherit requiresAuth from parent
   */
  {
    path: '/',
    component: DashboardLayout,
    name: 'Dashboard Layout',
    meta: { requiresAuth: true, nav: true },
    children: [
      /**
       * FELLOW/MENTOR/COMMON: User's own applications
       * GUIDE users are redirected to /applications/all by router guard
       * COMMON users have limited access (only their own applications)
       */
      {
        path: 'applications',
        component: UserApplicationsView,
        name: 'User Applications',
        meta: { requiresAuth: true, title: 'My applications' },
        beforeEnter: (
          to: RouteLocationNormalized,
          from: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => {
          // Reset filters when navigating to User Applications
          // This ensures filters from All Applications don't persist
          const applicationsStore = useApplicationsStore()

          // Get the page from URL query if it exists
          const page = to.query.page ? parseInt(to.query.page as string) : 1

          // Set the current page in the store WITHOUT triggering a fetch
          // (fetch will be handled by the UserApplicationsList component)
          applicationsStore.currentPage = page

          // Handle status filter if present in URL
          if (to.query.status) {
            applicationsStore.selectedStatus = to.query.status as string
          }

          next()
        },
        children: [
          {
            path: ':application_id',
            name: 'User Application container',
            component: UserApplicationDetails,
            props: true,
            meta: { requiresAuth: true, title: 'My application' }
          },
          {
            path: ':application_id/edit',
            name: 'User Application edit',
            component: UserApplicationEdit,
            props: true,
            meta: { requiresAuth: true, title: 'Edit application' }
          }
        ]
      },
      /**
       * FELLOW/MENTOR/COMMON: Create new application
       */
      {
        path: 'applications/create-new',
        component: UserApplicationAddContainer,
        name: 'Create new application',
        meta: { requiresAuth: true, title: 'New application' }
      },
      {
        path: 'my-referrals',
        name: 'My Referrals',
        component: MyReferralsView,
        meta: { requiresAuth: true, title: 'My Referrals' },
        beforeEnter: (
          to: RouteLocationNormalized,
          from: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => {
          const authStore = useAuthStore()
          if (!authStore.isReferrer) {
            return next({ name: 'PermissionDenied' })
          }
          next()
        },
        children: [
          {
            path: ':application_id',
            name: 'My Referrals Expanded Application',
            component: UserApplicationDetails,
            props: true,
            meta: { requiresAuth: true, title: 'My referral application' }
          }
        ]
      },
      /**
       * GUIDE-ONLY: All applications (management view)
       * Only accessible to GUIDE users
       */
      {
        path: 'applications/all',
        component: AllApplicationsView,
        name: 'All Applications',
        meta: {
          requiresAuth: true,
          requiresGuide: true,
          keepAlive: true,
          title: 'All applications'
        },
        beforeEnter: (
          to: RouteLocationNormalized,
          from: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => {
          const applicationsStore = useApplicationsStore()
          // Check URL query params to set initial filter state
          const statusQuery = to.query.status

          if (statusQuery && typeof statusQuery === 'string') {
            // If a specific status is requested (and not referral)
            applicationsStore.selectedStatus = statusQuery
          } else {
            // Default to exclude finished if no specific filter is set in URL
            applicationsStore.selectedStatus = ''
          }
          next()
        },
        children: [
          {
            path: ':application_id',
            name: 'All Applications Expanded',
            props: true,
            children: [
              {
                path: '',
                name: 'All Applications Expanded Application',
                component: UserApplicationDetails,
                props: { allApplications: true },
                meta: {
                  requiresAuth: true,
                  requiresGuide: true,
                  title: 'Application details'
                }
              },
              {
                path: 'edit',
                name: 'All Applications Expanded Edit',
                component: UserApplicationEdit,
                props: true,
                meta: {
                  requiresAuth: true,
                  requiresGuide: true,
                  title: 'Edit application'
                }
              },
              {
                path: 'referral-email',
                name: 'All Applications Expanded Referral email preview',
                component: ReferralEmailPreview,
                props: { allApplications: true },
                meta: {
                  requiresAuth: true,
                  requiresGuide: true,
                  requiresAdmin: true,
                  title: 'Referral preview'
                }
              }
            ]
          }
        ]
      },
      /**
       * ADMIN-ONLY (GUIDE): Referral requests management
       */
      {
        path: 'referral-requests',
        name: 'Referral requests',
        component: ReferralRequestsView,
        meta: {
          requiresAuth: true,
          requiresGuide: true,
          requiresAdmin: true,
          title: 'Referral requests'
        },
        children: [
          {
            path: ':request_id',
            name: 'Referral request details',
            component: ReferralRequestsView,
            props: true,
            meta: {
              requiresAuth: true,
              requiresGuide: true,
              requiresAdmin: true,
              title: 'Referral request details'
            }
          }
        ]
      },
      /**
       * Legacy route redirects - Redirect old /roles paths to /jobs
       */
      {
        path: 'roles',
        redirect: '/jobs',
        meta: { requiresAuth: true, title: 'All jobs' }
      },
      {
        path: 'roles/:role_id',
        redirect: (to: RouteLocationGeneric) => {
          const params = to.params as any
          return { path: `/jobs/${params.role_id}` }
        },
        meta: { requiresAuth: true, title: 'Job details' }
      },
      /**
       * PUBLIC (authenticated): Job listings and details
       * Accessible to all authenticated users (COMMON, MENTOR, FELLOW, GUIDE)
       */
      {
        path: 'jobs',
        name: 'All jobs',
        component: RolesView,
        props: true,
        meta: { requiresAuth: true, title: 'All jobs' },
        children: [
          {
            path: ':role_id',
            name: 'Job details',
            component: JobDetails,
            props: true,
            meta: {
              requiresAuth: true,
              keepAlive: true,
              title: 'Job details'
            }
          },
          /**
           * Job editing - Accessible to both GUIDE and FELLOW
           * Permission check in beforeEnter ensures only authorized users can edit
           */
          {
            path: ':role_id/edit',
            name: 'Role modal edit',
            meta: {
              requiresAuth: true,
              title: 'Edit job'
            },
            component: JobEditView,
            beforeEnter: (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next: NavigationGuardNext
            ) => {
              const { isGuide, isFellow } = useAuth()
              if (!isGuide.value && !isFellow.value) {
                return next({ path: from.path || '/' })
              }
              next()
            }
          },
          {
            path: ':role_id/:application_id',
            name: 'Job Application container',
            component: UserApplicationDetails,
            meta: {
              requiresAuth: true,
              keepAlive: true,
              title: 'Job application details'
            },
            props: true
          },
          /**
           * Job Application editing - Accessible to GUIDE or application owner
           * Permission check in beforeEnter ensures only authorized users can edit
           */
          {
            path: ':role_id/:application_id/edit',
            name: 'Job Application edit',
            component: JobApplicationEdit,
            meta: {
              requiresAuth: true,
              title: 'Edit job application'
            },
            beforeEnter: async (
              to: RouteLocationNormalized,
              from: RouteLocationNormalized,
              next: NavigationGuardNext
            ) => {
              const authStore = useAuthStore()

              // Check if authenticated
              if (!authStore.isAuthenticated) {
                return next('/login')
              }

              // Get role from reactive auth store
              const isGuide = authStore.isGuide

              // Get current user ID from token (still needed for ownership check)
              const token = localStorage.getItem('token')
              if (!token) return next('/login')

              const decodedToken = decodeJWT(token)
              if (!decodedToken) return next('/login')

              const currentUserId = decodedToken.id

              try {
                const applicationsStore = useApplicationsStore()
                // Fetch the specific application using the ID from the route params
                const application = await applicationsStore.fetchApplicationById(
                  to.params.application_id as string
                )

                // Check if the application exists and has user details
                if (!application || !application.user_details) {
                  console.error('Application or user details not found')
                  // Redirect to a safe place, maybe the job list or application view
                  return next(`/jobs/${to.params.role_id}/${to.params.application_id}`)
                }

                const applicationOwnerId = application.user_details.id

                // Allow access if user is a Guide OR is the owner of the application
                if (isGuide || currentUserId === applicationOwnerId) {
                  next() // Proceed to the route
                } else {
                  // If not a guide and not the owner, deny access
                  console.warn('User does not have permission to edit this application.')
                  // Redirect back or to a permission denied page
                  return next(`/jobs/${to.params.role_id}/${to.params.application_id}`)
                }
              } catch (error) {
                console.error('Error fetching application for edit guard:', error)
                // Handle error, maybe redirect back
                return next(`/jobs/${to.params.role_id}/${to.params.application_id}`)
              }
            }
          },
          /**
           * ADMIN-ONLY (GUIDE): Referral email preview for job applications
           */
          {
            path: ':role_id/:application_id/referral-email',
            name: 'Job Application Referral email preview',
            component: ReferralEmailPreview,
            props: { allApplications: false },
            meta: {
              requiresAuth: true,
              requiresGuide: true,
              requiresAdmin: true,
              title: 'Referral preview'
            }
          }
        ]
      },
      /**
       * PUBLIC (authenticated): Companies directory
       * Accessible to all authenticated users
       */
      {
        path: 'companies',
        name: 'Companies',
        component: CompaniesView,
        meta: { requiresAuth: true, title: 'Companies' }
      },
      /**
       * FELLOW/MENTOR/GUIDE: Create new job posting
       * NOT accessible to COMMON users (handled by beforeEnter)
       */
      {
        path: 'jobs/create-new',
        name: 'Add Role modal',
        component: JobCreateView,
        meta: {
          requiresAuth: true,
          title: 'New job'
        }
      },
      /**
       * FELLOW/MENTOR: Fellow applicants list
       * NOT accessible to COMMON users (handled by router guard)
       * GUIDE users can also access this
       */
      {
        path: 'applicants',
        name: 'Applicants',
        component: ApplicantsView,
        meta: {
          requiresAuth: true,
          title: 'Applicants'
        }
      },
      /**
       * FELLOW/MENTOR-ONLY: Current user's applicant profile
       * NOT accessible to COMMON or GUIDE users (handled by router guard)
       */
      {
        path: 'applicant',
        name: 'User applicant profile',
        component: ApplicantView,
        meta: {
          requiresAuth: true,
          title: 'My applicant profile'
        },
        beforeEnter: async (
          to: RouteLocationNormalized,
          from: RouteLocationNormalized,
          next: NavigationGuardNext
        ) => {
          const { useUserStore } = await import('@/stores/user')
          const userStore = useUserStore()

          try {
            // Ensure we have current user data
            if (!userStore.currentUser) {
              await userStore.fetchCurrentUser()
            }

            if (userStore.currentUser?.id) {
              // Set the current user's ID as a prop to the component
              to.params.id = userStore.currentUser.id.toString()
              next()
            } else {
              // If no user data, redirect to login
              next('/login')
            }
          } catch (error) {
            console.error('Error fetching current user for applicant route:', error)
            next('/login')
          }
        },
        props: true
      },
      /**
       * FELLOW/MENTOR/GUIDE: View specific applicant profile
       * NOT accessible to COMMON users (handled by router guard)
       */
      {
        path: 'applicant/:id',
        name: 'Applicant Profile',
        component: ApplicantView,
        props: true,
        meta: {
          requiresAuth: true,
          title: 'Applicant profile'
        }
      },
      {
        path: 'applicant/:id/:role_id',
        name: 'Applicant Profile with Role',
        component: ApplicantView,
        props: true,
        meta: {
          requiresAuth: true,
          title: 'Applicant profile'
        }
      },
      /**
       * GUIDE-ONLY: Recently active applicants
       */
      {
        path: 'applicants/recently-active',
        name: 'Recently active applicants',
        component: RecentlyActiveCandidates,
        meta: {
          requiresAuth: true,
          requiresGuide: true,
          title: 'Recently active applicants'
        }
      },
      /**
       * GUIDE-ONLY: Job application stats by batch (pod/expedition) or by hand-picked people
       */
      {
        path: 'job-search-progress',
        name: 'Job Search Progress',
        component: () => import('@/views/manage/GuideStatsView.vue'),
        meta: {
          requiresAuth: true,
          requiresGuide: true,
          title: 'Job Search Progress'
        }
      },
      {
        path: 'guide',
        redirect: (to: RouteLocationGeneric) => ({ path: '/job-search-progress', query: to.query })
      },
      /**
       * GUIDE-ONLY: Resume files management
       */
      {
        path: 'resumes',
        name: 'Resumes',
        component: () => import('@/views/dashboard/ResumesView.vue'),
        meta: {
          requiresAuth: true,
          requiresGuide: true,
          title: 'Resumes'
        }
      },
      /**
       * ADMIN-ONLY (GUIDE): Referrers management
       */
      {
        path: 'referrers',
        meta: {
          requiresAuth: true,
          requiresGuide: true,
          requiresAdmin: true,
          title: 'Referrers'
        },
        children: [
          {
            path: '',
            name: 'Referrers',
            component: ReferrersView,
            meta: {
              requiresAuth: true,
              requiresGuide: true,
              requiresAdmin: true,
              title: 'Referrers'
            }
          },
          {
            path: 'add-referrer',
            name: 'Add new referrer',
            component: ReferrerAddView,
            meta: {
              requiresAuth: true,
              requiresGuide: true,
              requiresAdmin: true,
              title: 'Add new referrer'
            }
          }
        ]
      }
    ]
  },
  /**
   * PUBLIC (authenticated): User settings
   * Accessible to both GUIDE and FELLOW users
   */
  {
    path: '/settings',
    name: 'Settings',
    component: SettingsView,
    meta: {
      requiresAuth: true,
      title: 'Settings'
    }
  },
  /**
   * PUBLIC (unauthenticated): Authentication routes
   */
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { title: 'Login' }
  },
  {
    path: '/login/password',
    name: 'PasswordLogin',
    component: PasswordLoginView,
    meta: { title: 'Sign in with password' }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
    meta: { title: 'Register' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPasswordView,
    meta: { title: 'Forgot password' }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPasswordView,
    props: true, // Accept the token as a prop
    meta: { title: 'Reset password' }
  },
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: AuthCallbackView,
    meta: { title: 'Authentication' }
  },
  {
    path: '/complete-profile',
    name: 'CompleteProfile',
    component: CompleteProfileView,
    meta: { title: 'Complete profile' }
  },
  {
    path: '/app/magic-login',
    name: 'MagicLogin',
    alias: '/magic-login',
    component: MagicLoginView,
    meta: { title: 'Magic sign in' }
  },
  /**
   * PUBLIC: Permission denied page
   */
  {
    path: '/permission-denied',
    name: 'PermissionDenied',
    component: PermissionDeniedView,
    meta: { title: 'Permission denied' }
  },
  /**
   * PUBLIC: Referral action pages (no auth required, token-based)
   */
  {
    path: '/referral-action/result',
    name: 'ReferralActionResult',
    component: ReferralActionResultView,
    meta: { title: 'Referral Action' }
  },
  {
    path: '/referral-action/:token',
    name: 'ReferralActionProvideLink',
    component: ReferralActionProvideLinkView,
    meta: { title: 'Submit Referral Link' }
  },
  /**
   * PUBLIC: Legal pages
   */
  {
    path: '/terms-and-conditions',
    name: 'Terms and conditions',
    component: TermsAndConditionsView,
    meta: { title: 'Terms and conditions' }
  },
  {
    path: '/privacy-policy',
    name: 'Privacy policy',
    component: PrivacyPolicyView,
    meta: { title: 'Privacy policy' }
  },
  /**
   * PUBLIC (unauthenticated): Explore jobs — no auth required
   */
  {
    path: '/explore',
    component: PublicJobsLayout,
    children: [
      {
        path: 'jobs',
        name: 'Public Jobs',
        component: RolesView,
        meta: { title: 'Job Opportunities' },
        children: [
          {
            path: ':role_id',
            name: 'Public Job Details',
            component: JobDetails,
            meta: { title: 'Job details' }
          }
        ]
      }
    ]
  },
  /**
   * Catch-all route for 404 errors
   */
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFoundView,
    meta: { title: 'Not found' }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
  }
})

/**
 * Navigation Guard - Handles authentication and role-based access control
 *
 * Supported roles: COMMON, MENTOR, FELLOW, GUIDE
 *
 * Flow:
 * 1. Check if route is public (no auth required)
 * 2. Verify authentication status (handles cross-subdomain scenarios)
 * 3. Get user role from reactive auth store (synced with JWT token)
 * 4. Enforce route access restrictions (requiresGuide, COMMON on applicant routes)
 * 5. Subscription + platform access (GET /subscriptions/access/status/) — same idea as Launchpad
 */
// eslint-disable-next-line complexity
router.beforeEach(async (to, from, next) => {
  if (from.query.blur === '1' && to.query.blur !== '1' && to.path !== from.path) {
    return next({
      path: to.path,
      query: {
        ...to.query,
        blur: '1'
      },
      hash: to.hash
    })
  }

  // Public pages that don't require authentication
  const authRequired = !PUBLIC_PAGE_NAMES.includes(to.name as any)

  const authStore = useAuthStore()

  // Check authentication status
  // Only check auth if the route requires it or the user might already be authenticated
  // Skipping checkAuth() for public routes prevents the API interceptor from
  // redirecting unauthenticated users to /login when the session check fails
  let isAuthenticated = authStore.isAuthenticated

  if (!isAuthenticated && authRequired) {
    // checkAuth() will also sync the role from token
    isAuthenticated = await authStore.checkAuth()
  }

  // Get user role from reactive auth store
  // The store's userRole is kept in sync with the JWT token
  // This ensures role checks always reflect the current token's role
  const userRole = authStore.userRole

  // Redirect authenticated users away from login page
  if ((to.name === 'Login' || to.name === 'PasswordLogin') && isAuthenticated) {
    // Redirect to root, which will handle role-based redirects
    return next('/')
  }

  // Enforce authentication requirement
  if (authRequired && !isAuthenticated) {
    localStorage.setItem('redirectAfterLogin', to.fullPath)
    return next({ name: 'Login', query: { redirect: to.fullPath } })
  }

  // Enforce GUIDE-only route restrictions
  // Routes with meta.requiresGuide can only be accessed by GUIDE users
  if (to.meta.requiresGuide && userRole !== 'GUIDE') {
    return next({ name: 'PermissionDenied' })
  }

  // Enforce ADMIN-only route restrictions
  // Routes with meta.requiresAdmin require the is_admin flag on the JWT
  if (to.meta.requiresAdmin && !authStore.isAdmin) {
    return next({ name: 'PermissionDenied' })
  }

  // Restrict GUIDE users from the personal applicant profile page
  // /applicant (own profile) is FELLOW/MENTOR only — redirect GUIDEs to their main view
  if (userRole === 'GUIDE' && to.path === '/applicant') {
    return next({ path: '/applications/all' })
  }

  // Restrict COMMON users from FELLOW/MENTOR-only routes
  // COMMON users can only access: /jobs, /applications (their own), /settings
  if (userRole === 'COMMON') {
    // Block access to applicant profile routes (FELLOW/MENTOR only)
    if (to.path === '/applicant' || to.path.startsWith('/applicant/')) {
      return next({ name: 'PermissionDenied' })
    }
    // Block access to applicants list (FELLOW/MENTOR only)
    if (to.path === '/applicants' || to.path.startsWith('/applicants/')) {
      return next({ name: 'PermissionDenied' })
    }
  }

  // Fetch subscription/platform status for authenticated non-public routes.
  // This enables isLimitedUser for Fellows (no tuition and no TrackTalent in platforms).
  // We intentionally do NOT block access here — limited users get a restricted
  // experience rather than a PermissionDenied page.
  if (isAuthenticated && !shouldSkipPlatformAccessCheck(to)) {
    const subscriptionStore = useSubscriptionStore()
    if (!subscriptionStore.isCacheFresh()) {
      await subscriptionStore.checkStatus({ preserveStateOnNetworkError: true })
    }
  }

  // Determine if this is a "limited" user. Match useAuth isLimitedUser.
  const subscriptionStore = useSubscriptionStore()
  const isLimitedUser =
    userRole === 'COMMON' ||
    (userRole === 'FELLOW' &&
      subscriptionStore.isInitialized &&
      !subscriptionStore.hasAccess &&
      !subscriptionStore.hasPlatform(DEFAULT_APP_PLATFORM))

  // Block limited users from program-only routes
  if (isLimitedUser) {
    const blockedPrefixes = [
      '/applicant',
      '/applicants',
      '/my-referrals',
      '/jobs/create-new',
      '/referral-requests',
      '/referrers',
    ]
    for (const prefix of blockedPrefixes) {
      if (to.path === prefix || to.path.startsWith(prefix + '/')) {
        return next({ name: 'PermissionDenied' })
      }
    }
  }

  // Set page title
  const pageTitle = to.meta.title
    ? `${to.meta.title} | TrackTalent`
    : 'TrackTalent | Outtalent'
  document.title = pageTitle as string

  next()
})

export default router
